/**
 * Blocks shared by the förhandling family (förhandlingsordning,
 * förhandlingsöverenskommelse, förhandlingsframställan, strandningsmeddelande),
 * built on the common kit.
 *
 * Every printed party/property datum is a slot value. A slot may carry an
 * autofill binding — resolved by the framework's `resolveAutofill` for
 * Regta's editor, ignored by an API consumer that passes the strings — but
 * a block never formats `ctx.parties` / `ctx.properties` into text itself.
 * The compile context is used for structure only: which signers exist for a
 * signature panel.
 */
import { partyDetailsBlock, pick, signingBlockDefinition } from '../common/index.js';
/** The option with this code; throws on a typo so a bad codebook fails at import. */
export function optionByCode(options, code) {
    const found = pick(options, code);
    if (!found || found.code !== code)
        throw new Error(`Unknown option code "${code}"`);
    return found;
}
/* ------------------------------------------------------------------ */
/* Party panels                                                       */
/* ------------------------------------------------------------------ */
/**
 * One party as a point of label | value rows (the legacy `PartyBlock`,
 * canonical ADR-0065 labels). A composition fixes `ref` and `title` as
 * instance input; `columns: 2` pairs the rows (a full-width Hyresvärdspart),
 * `beside_previous` puts the point beside the one before it and
 * `variant: 'clause'` matches a document whose points use the compact
 * clause heading (the förhandlingsordning).
 */
const partyPanel = (spec) => {
    const single = partyDetailsBlock({ ...spec, ordinal: 0 });
    const paired = partyDetailsBlock({ ...spec, ordinal: 0, columns: 2 });
    return {
        ...single,
        compile: (input, ctx) => {
            const out = (input.columns === 2 ? paired : single).compile(input, ctx);
            if (input.variant !== 'clause' || !Array.isArray(out))
                return out;
            return out.map((node) => (node.type === 'section' ? { ...node, variant: 'clause' } : node));
        },
    };
};
/** Hyresvärdspart — legacy projection has no idHint, hence "Personnr / Org.nr". */
export const partHyresvardspart = partyPanel({
    blockKey: 'forhandling.part-hyresvardspart',
    title: 'Hyresvärdspart',
    summary: 'The landlord party’s name, id number and contact details.',
    role: 'lessor',
    ref: 'fo-part-hyresvard',
    fields: [
        { key: 'landlord_name', label: 'Namn', field: 'name', required: true, help: 'Fullständigt namn eller företagsnamn' },
        { key: 'landlord_org_number', label: 'Personnr / Org.nr', field: 'legalId', required: true },
        { key: 'landlord_address', label: 'Adress', field: 'address', required: true },
        { key: 'landlord_email', label: 'E-post', field: 'email', required: true },
        { key: 'landlord_phone', label: 'Telefon', field: 'phone' },
    ],
});
export const partHyresgastorganisation = partyPanel({
    blockKey: 'forhandling.part-hyresgastorganisation',
    title: 'Hyresgästorganisation',
    summary: 'The tenant organisation’s name, organisation number and contact details.',
    role: 'lessee',
    ref: 'fo-part-hyresgastorg',
    fields: [
        { key: 'tenant_org_name', label: 'Namn', field: 'name', required: true, help: 'Hyresgästorganisationens namn' },
        { key: 'tenant_org_number', label: 'Organisationsnummer', field: 'legalId', required: true },
        { key: 'tenant_org_address', label: 'Adress', field: 'address', required: true },
        { key: 'tenant_org_email', label: 'E-post', field: 'email', required: true },
        { key: 'tenant_org_phone', label: 'Telefon', field: 'phone' },
    ],
});
export const partFastighetsagarforening = partyPanel({
    blockKey: 'forhandling.part-fastighetsagarforening',
    title: 'Fastighetsägarförening',
    summary: 'The property-owner association’s name, organisation number and contact details.',
    role: 'other',
    ref: 'fo-part-fastighetsagarforening',
    fields: [
        { key: 'property_assoc_name', label: 'Namn', field: 'name', required: true, help: 'Föreningens namn' },
        { key: 'property_assoc_org_number', label: 'Organisationsnummer', field: 'legalId', required: true },
        { key: 'property_assoc_address', label: 'Adress', field: 'address', required: true },
        { key: 'property_assoc_email', label: 'E-post', field: 'email', required: true },
        { key: 'property_assoc_phone', label: 'Telefon', field: 'phone' },
    ],
});
/* ------------------------------------------------------------------ */
/* Signing                                                            */
/* ------------------------------------------------------------------ */
/**
 * `forhandling.signing@v1` — the shared legacy SigningBlock under the
 * family's key, so issued compositions keep resolving. Parties, `statement`,
 * `lead`/`lead_strong` and the section frame are instance input; place/date
 * and the digital wording follow `ctx.custom.signing`.
 */
export const signing = signingBlockDefinition({
    blockKey: 'forhandling.signing',
    summary: 'Signature panels with the legacy execution statement and optional Ort/Datum rows.',
});
export const FAMILY_BLOCKS = [partHyresvardspart, partHyresgastorganisation, partFastighetsagarforening, signing];
//# sourceMappingURL=family.js.map